import type { SafeString } from '@ember/template';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';

import type { PlottedPoint } from '../../../types/components/prism/data-bar.ts';

type DataBarPoint = {
  isMainPoint?: boolean;
  position: number;
  style?: SafeString;
  value: number;
};

interface PrismDataBarDataBarPlottedPointsSignature {
  Args: {
    data: PlottedPoint[];
    isAbsolute?: boolean;
    valueFormatter?: (value: number) => string;
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarPlottedPoints extends Component<PrismDataBarDataBarPlottedPointsSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calculatedPositions() {
    const positions: Record<string, PlottedPoint[]> = {};

    this.sortedDataBarItems.forEach((dataBarItem) => {
      // toString since int can't be keys
      if (positions[dataBarItem?.value?.toString()]) {
        positions[dataBarItem.value.toString()]?.push(dataBarItem);
      } else {
        positions[dataBarItem.value.toString()] = [dataBarItem];
      }
    });

    const dataBarPoints: DataBarPoint[] = [];

    if (this.sortedDataBarItems.length) {
      // Since multiple positions can be the same, we need to store an offset to add to the other ones
      let offsetPosition = 0;

      // Since we want to group similar values on the progress bar, we'll need to calculate the total offset
      // to do this we need 2 things:
      // - the maximum value
      // - the total amount of duplicate values or the totalOffset
      let totalOffset = 0;

      // The min and max can just be taken out of the sorted array
      const min = this.sortedDataBarItems[0]?.value ?? 0;
      const max =
        this.sortedDataBarItems[this.sortedDataBarItems.length - 1]?.value ?? 0;

      Object.keys(positions).forEach((position) => {
        // We multiply by two since the offset between items is 2%
        totalOffset += ((positions[position]?.length ?? 1) - 1) * 2;
      });

      // Use 0 as reference value if min > 0 and 5(%) as reference if max < 5(%) (same as for minValue and maxValue)
      let defaultMaxValue = this.args.isAbsolute ? 5 : 0.05;
      if (min < 0) {
        defaultMaxValue = 0; // maxValue=0 is allowed if we are dealing with negative numbers (POS)
      }

      const minMaxRange = Math.max(max, defaultMaxValue) - Math.min(min, 0);

      // The normalisationFactor will help us determine the % on the progress bar
      // So since we need to include the offsets here, we subtract the totalOffset instead of just have it be based on 100%
      // Double check we are not dividing by 0
      const normalisationFactor =
        minMaxRange !== 0 ? (100 - totalOffset) / minMaxRange : 1;

      let positionShift = 0;
      if (min < 0) {
        // In case there are negative values: the lowest negative value should be considered as the starting point (0 by default)
        positionShift = 100 - totalOffset;
        if (max > 0) {
          // In case there are both positive and negative values: only the negative end of the progress bar should be compsensated in the shift
          const relativeNegativeShift = Math.abs(min) / (Math.abs(min) + max);
          positionShift *= relativeNegativeShift;
        }
      }

      Object.keys(positions)
        .sort()
        .forEach((position) => {
          if (positions[position]?.length) {
            const hasMainPoint = positions[position]?.some(
              (dataBarItem) => dataBarItem.isMainPoint,
            );
            positions[position]?.forEach((dataBarItem, index) => {
              if (index > 0) {
                offsetPosition += 2; // If there is more than one for this position, increase the offset
              }
              const pointPosition =
                dataBarItem.value * normalisationFactor +
                offsetPosition +
                positionShift;
              dataBarPoints.push({
                position: pointPosition,
                value: dataBarItem.value,
                style: htmlSafe(`left: ${pointPosition}%;`),
                isMainPoint: false,
              });
            });
            if (hasMainPoint) {
              // The main point needs to be the middle, so if the main point has this position, assign the middle value as the main tick (if even, it's be more to the left but this is a best effort calculation)
              const positionsLength = positions[position]?.length ?? 0;
              const middleIndex =
                dataBarPoints.length - Math.ceil(positionsLength / 2);
              const dataBarItem = dataBarPoints[middleIndex];
              if (dataBarItem) {
                dataBarItem.isMainPoint = true;
              }
            }
          }
        });
    }

    return dataBarPoints;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMaxValue() {
    const formattedMaxValue =
      this.args.valueFormatter?.(this.maxValue) ?? this.maxValue;
    return formattedMaxValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMinValue() {
    const formattedMinValue =
      this.args.valueFormatter?.(this.minValue) ?? this.minValue;
    return formattedMinValue;
  }

  // Avoid progressbars ranging from 0 to 0
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxValue() {
    // The default max value is 5 for absolute values and 5% for relative values
    const defaultMaxValue = this.args.isAbsolute ? 5 : 0.05;
    const maxValue =
      this.sortedDataBarItems[this.sortedDataBarItems.length - 1]?.value ?? 0;

    // Fall back on default value if data array is empty
    if (!this.sortedDataBarItems.length) {
      return defaultMaxValue;
    }

    if (this.minValue < 0) {
      // maxValue=0 is allowed if we are dealing with negative numbers (POS)
      return maxValue;
    }

    return Math.max(maxValue, defaultMaxValue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minValue() {
    const defaultMinValue = 0;
    const minValue = this.sortedDataBarItems[0]?.value ?? 0;

    // Fall back on default value if data array is empty
    if (!this.sortedDataBarItems.length) {
      return defaultMinValue;
    }

    // Use the min value in sortedDataBarItems if it's below 0
    return Math.min(minValue, defaultMinValue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedDataBarItems() {
    return this.args.data.sort((a, b) => a.value - b.value);
  }

  <template>
    <div class="prism-data-bar-plotted-points-container">
      <p class="data-bar-plotted-points-value text-200--medium">
        {{this.formattedMinValue}}
      </p>
      <div class="data-bar-plotted-points">
        <div class="data-bar__inner-container">
          {{#each this.calculatedPositions as |position|}}
            <div
              class="data-bar__item {{if position.isMainPoint 'main-point'}}"
              style={{position.style}}
            >
            </div>
          {{/each}}
        </div>
      </div>
      <p class="data-bar-plotted-points-value text-200--medium">
        {{this.formattedMaxValue}}
      </p>
    </div>
  </template>
}
